import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useToast } from '../../Toast';

interface RetentionSettings {
  recordRetentionYears: number;
  recordingRetentionDays: number;
  transcriptRetentionDays: number;
  autoDeleteRecordings: boolean;
  retainAfterSigned: boolean;
}

const DEFAULT_SETTINGS: RetentionSettings = {
  recordRetentionYears: 7,
  recordingRetentionDays: 365,
  transcriptRetentionDays: 365,
  autoDeleteRecordings: false,
  retainAfterSigned: true,
};

const DataRetention: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [settings, setSettings] = useState<RetentionSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.uid) return;
      try {
        const providerDoc = await getDoc(doc(db, 'providers', user.uid));
        if (providerDoc.exists()) {
          const data = providerDoc.data();
          if (data.retentionSettings) {
            setSettings({ ...DEFAULT_SETTINGS, ...data.retentionSettings });
          }
        }
      } catch (err) {
        // Fall back to defaults
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [user?.uid]);

  const handleSave = async () => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'providers', user.uid), {
        retentionSettings: settings,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error('Save Failed', 'Failed to save retention settings. Please try again.');
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const handleChange = (key: keyof RetentionSettings, value: number | boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', color: '#87CEEB' }}>Loading retention settings...</div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: 0 }}>
          Data Retention
        </h1>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: '8px 0 0 0' }}>
          Configure how long clinical records, session recordings, and transcripts are retained.
          HIPAA defers to state law — most states require 6-10 years for clinical records.
        </p>
      </div>

      {/* Success alert */}
      {saved && (
        <div role="alert" aria-live="polite" style={{
          backgroundColor: '#059669',
          color: '#FFFFFF',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          Settings saved successfully!
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Clinical Records */}
        <section style={{
          backgroundColor: '#1A4D6F',
          borderRadius: '8px',
          padding: '24px',
          border: '1px solid #2A5470'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Clinical Records
          </h2>
          <p style={{ fontSize: '13px', color: '#B0C4DE', margin: '0 0 20px 0' }}>
            Progress notes, treatment plans, diagnoses, and prescriptions.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label htmlFor="record-retention" style={{ color: '#FFFFFF', fontSize: '14px', minWidth: '180px' }}>
              Retain records for
            </label>
            <select
              id="record-retention"
              value={settings.recordRetentionYears}
              onChange={(e) => handleChange('recordRetentionYears', Number(e.target.value))}
              style={{
                background: '#374151',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                padding: '8px 12px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            >
              <option value={6}>6 years</option>
              <option value={7}>7 years (recommended)</option>
              <option value={10}>10 years</option>
              <option value={15}>15 years</option>
              <option value={0}>Indefinitely</option>
            </select>
          </div>
          <p style={{ fontSize: '12px', color: '#87CEEB', margin: '8px 0 0 192px' }}>
            Check your state's requirements. Most states require 6-10 years after last encounter.
          </p>
        </section>

        {/* Session Recordings */}
        <section style={{
          backgroundColor: '#1A4D6F',
          borderRadius: '8px',
          padding: '24px',
          border: '1px solid #2A5470'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Session Recordings
          </h2>
          <p style={{ fontSize: '13px', color: '#B0C4DE', margin: '0 0 20px 0' }}>
            Video and audio recordings of telehealth sessions.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <label htmlFor="recording-retention" style={{ color: '#FFFFFF', fontSize: '14px', minWidth: '180px' }}>
              Retain recordings for
            </label>
            <select
              id="recording-retention"
              value={settings.recordingRetentionDays}
              onChange={(e) => handleChange('recordingRetentionDays', Number(e.target.value))}
              style={{
                background: '#374151',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                padding: '8px 12px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year (recommended)</option>
              <option value={0}>Indefinitely</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <label htmlFor="auto-delete" style={{ color: '#FFFFFF', fontSize: '14px', minWidth: '180px' }}>
              Auto-delete after period
            </label>
            <button
              id="auto-delete"
              onClick={() => handleChange('autoDeleteRecordings', !settings.autoDeleteRecordings)}
              role="switch"
              aria-checked={settings.autoDeleteRecordings}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: '13px',
                border: 'none',
                backgroundColor: settings.autoDeleteRecordings ? '#059669' : '#374151',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <span style={{
                display: 'block',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                position: 'absolute',
                top: '3px',
                left: settings.autoDeleteRecordings ? '25px' : '3px',
                transition: 'left 0.2s'
              }} />
            </button>
          </div>
          {settings.autoDeleteRecordings && (
            <p style={{ fontSize: '12px', color: '#FCA5A5', margin: '0 0 0 192px' }}>
              Recordings will be permanently deleted after the retention period. This cannot be undone.
            </p>
          )}
        </section>

        {/* Transcripts */}
        <section style={{
          backgroundColor: '#1A4D6F',
          borderRadius: '8px',
          padding: '24px',
          border: '1px solid #2A5470'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Session Transcripts
          </h2>
          <p style={{ fontSize: '13px', color: '#B0C4DE', margin: '0 0 20px 0' }}>
            AI-generated transcriptions of telehealth sessions.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <label htmlFor="transcript-retention" style={{ color: '#FFFFFF', fontSize: '14px', minWidth: '180px' }}>
              Retain transcripts for
            </label>
            <select
              id="transcript-retention"
              value={settings.transcriptRetentionDays}
              onChange={(e) => handleChange('transcriptRetentionDays', Number(e.target.value))}
              style={{
                background: '#374151',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                padding: '8px 12px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year (recommended)</option>
              <option value={0}>Indefinitely</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label htmlFor="retain-signed" style={{ color: '#FFFFFF', fontSize: '14px', minWidth: '180px' }}>
              Keep until notes are signed
            </label>
            <button
              id="retain-signed"
              onClick={() => handleChange('retainAfterSigned', !settings.retainAfterSigned)}
              role="switch"
              aria-checked={settings.retainAfterSigned}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: '13px',
                border: 'none',
                backgroundColor: settings.retainAfterSigned ? '#059669' : '#374151',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <span style={{
                display: 'block',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                position: 'absolute',
                top: '3px',
                left: settings.retainAfterSigned ? '25px' : '3px',
                transition: 'left 0.2s'
              }} />
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#87CEEB', margin: '8px 0 0 192px' }}>
            When enabled, transcripts are always retained until the clinical note is signed, regardless of the retention period above.
          </p>
        </section>
      </div>

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '32px'
      }}>
        <button
          onClick={handleReset}
          style={{
            padding: '10px 20px',
            borderRadius: '6px',
            border: '1px solid #6CA6C1',
            backgroundColor: 'transparent',
            color: '#87CEEB',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Reset to defaults
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: '10px 20px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Save changes
        </button>
      </div>
    </div>
  );
};

export default DataRetention;
